import React, { useEffect, useState } from "react";
import { deleteTape, updateTape } from "../lib/tapes";
import { releaseKeyFor, subscribeToRelease, subscribeToUserReleaseRating, submitReleaseRating } from "../lib/releases";
import { T } from "../theme";
import { Badge, Btn, Card, fmt, SectionTitle, StarRating, TapeIcon } from "../ui";

export default function TapeDetailScreen({ tape, userId, onBack, onEdit, onListForSale }) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const hasCostBasis = tape.purchasePrice != null;
  const gain = hasCostBasis ? Number(tape.estimatedValue || 0) - Number(tape.purchasePrice) : null;

  const allPhotos = [tape.mainPhotoUrl, ...(tape.photoUrls || [])].filter(Boolean);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const activePhoto = allPhotos[activePhotoIndex] || null;

  const releaseId = releaseKeyFor(tape.artist, tape.albumTitle);
  const [release, setRelease] = useState(null);
  const [myReleaseRating, setMyReleaseRating] = useState(0);

  useEffect(() => {
    const unsubRelease = subscribeToRelease(releaseId, setRelease);
    const unsubMine = subscribeToUserReleaseRating(releaseId, userId, setMyReleaseRating);
    return () => { unsubRelease(); unsubMine(); };
  }, [releaseId, userId]);

  const setPersonalRating = (n) => updateTape(tape.id, { personalRating: n });
  const setCommunityRating = (n) => submitReleaseRating(releaseId, userId, tape.artist, tape.albumTitle, n);

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await deleteTape(tape.id);
      onBack();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <Card style={{ background: `linear-gradient(135deg,${T.surface},${T.card})`, textAlign: "center", padding: "24px 16px" }}>
        {activePhoto ? (
          <img src={activePhoto} alt={tape.albumTitle} style={{ width: 160, height: 160, objectFit: "cover", borderRadius: 10, border: `1px solid ${T.border}` }} />
        ) : (
          <TapeIcon size={80} />
        )}
        {allPhotos.length > 1 && (
          <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
            {allPhotos.map((url, i) => (
              <img
                key={i}
                src={url}
                alt=""
                onClick={() => setActivePhotoIndex(i)}
                style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 6, cursor: "pointer", border: `2px solid ${i === activePhotoIndex ? T.accent : "transparent"}`, opacity: i === activePhotoIndex ? 1 : 0.7 }}
              />
            ))}
          </div>
        )}
        <div style={{ fontSize: 22, fontWeight: "bold", color: T.text, marginTop: 12 }}>{tape.albumTitle}</div>
        <div style={{ fontSize: 14, color: T.muted, marginTop: 4 }}>{tape.artist}</div>
        <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
          {tape.releaseYear || ""}{tape.recordLabel && ` · ${tape.recordLabel}`}{tape.catalogNumber && ` · ${tape.catalogNumber}`}
        </div>
        <div style={{ marginTop: 10, display: "flex", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
          {tape.condition && <Badge condition={tape.condition} />}
          {tape.cartridgeColor && <span style={{ fontSize: 9, background: T.statBg, border: `1px solid ${T.border}`, color: T.muted, padding: "2px 7px", borderRadius: 4 }}>{tape.cartridgeColor}</span>}
          {tape.variation && <span style={{ fontSize: 9, background: T.statBg, border: `1px solid ${T.border}`, color: T.muted, padding: "2px 7px", borderRadius: 4 }}>{tape.variation}</span>}
          {tape.countryOfManufacture && tape.countryOfManufacture !== "Unknown" && tape.countryOfManufacture === tape.countryOfRelease && (
            <span style={{ fontSize: 9, background: T.statBg, border: `1px solid ${T.border}`, color: T.muted, padding: "2px 7px", borderRadius: 4 }}>{tape.countryOfManufacture} Pressing</span>
          )}
          {tape.countryOfManufacture && tape.countryOfManufacture !== "Unknown" && tape.countryOfManufacture !== tape.countryOfRelease && (
            <span style={{ fontSize: 9, background: T.statBg, border: `1px solid ${T.border}`, color: T.muted, padding: "2px 7px", borderRadius: 4 }}>Made in {tape.countryOfManufacture}</span>
          )}
          {tape.countryOfRelease && tape.countryOfRelease !== "Unknown" && tape.countryOfManufacture !== tape.countryOfRelease && (
            <span style={{ fontSize: 9, background: T.statBg, border: `1px solid ${T.border}`, color: T.muted, padding: "2px 7px", borderRadius: 4 }}>Released in {tape.countryOfRelease}</span>
          )}
        </div>
      </Card>

      <SectionTitle>Ratings</SectionTitle>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 9, color: T.muted, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 4 }}>Your Rating</div>
            <StarRating value={tape.personalRating || 0} onChange={setPersonalRating} />
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 9, color: T.muted, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 4 }}>Community Average</div>
            <div style={{ fontSize: 16, fontWeight: "bold", color: T.accent }}>
              {release?.ratingCount ? `${release.ratingAverage.toFixed(1)} ★` : "No ratings yet"}
            </div>
            {release?.ratingCount > 0 && <div style={{ fontSize: 9, color: T.muted }}>{release.ratingCount} collector{release.ratingCount === 1 ? "" : "s"}</div>}
          </div>
        </div>
        <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
          <div style={{ fontSize: 9, color: T.muted, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 4 }}>Rate This Release for the Community</div>
          <StarRating value={myReleaseRating} onChange={setCommunityRating} />
        </div>
      </Card>

      <SectionTitle>Purchase and Value</SectionTitle>
      <Card>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, textAlign: "center" }}>
          {[
            ["Paid", hasCostBasis ? fmt(tape.purchasePrice) : tape.inherited ? "Inherited" : "—", T.muted],
            ["Est. Value", fmt(tape.estimatedValue), T.value],
            ["Gain", hasCostBasis ? `${gain >= 0 ? "+" : ""}${fmt(gain)}` : "—", hasCostBasis ? (gain >= 0 ? T.green : "#C45A2D") : T.muted],
          ].map(([l, v, c]) => (
            <div key={l}>
              <div style={{ fontSize: 9, color: T.muted, letterSpacing: "0.15em", textTransform: "uppercase" }}>{l}</div>
              <div style={{ fontSize: 16, fontWeight: "bold", color: c, marginTop: 4 }}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 9, color: T.muted, marginTop: 10 }}>Collector-entered estimate, not a professional appraisal.</div>
        {(tape.purchaseDate || tape.purchaseSource) && (
          <div style={{ marginTop: 10, fontSize: 11, color: T.muted }}>
            {tape.purchaseDate && <div>{tape.inherited ? "Received" : "Purchased"}: {tape.purchaseDate}</div>}
            {tape.purchaseSource && <div>{tape.inherited ? "From" : "Source"}: {tape.purchaseSource}</div>}
          </div>
        )}
      </Card>

      {tape.trackListing?.length > 0 && (
        <>
          <SectionTitle>Track / Program Listing</SectionTitle>
          <Card>
            {tape.trackListing.map((tr, i) => (
              <div key={i} style={{ display: "flex", gap: 10, padding: "6px 0", borderBottom: i < tape.trackListing.length - 1 ? `1px solid ${T.border}` : "none" }}>
                <span style={{ fontSize: 11, color: T.muted, minWidth: 20, fontFamily: "monospace" }}>{i + 1}.</span>
                <span style={{ fontSize: 13, color: T.text }}>{tr}</span>
              </div>
            ))}
          </Card>
        </>
      )}

      {tape.notes && (
        <>
          <SectionTitle>Notes</SectionTitle>
          <Card><div style={{ fontSize: 13, color: T.muted, lineHeight: 1.7, fontStyle: "italic" }}>{tape.notes}</div></Card>
        </>
      )}

      {confirming ? (
        <Card style={{ textAlign: "center" }}>
          <div style={{ fontSize: 13, color: T.text, marginBottom: 12 }}>Delete this tape? This cannot be undone.</div>
          <div style={{ display: "flex", gap: 10 }}>
            <Btn variant="outline" onClick={() => setConfirming(false)} style={{ flex: 1 }}>Cancel</Btn>
            <Btn variant="danger" disabled={deleting} onClick={confirmDelete} style={{ flex: 1 }}>{deleting ? "Deleting…" : "Delete"}</Btn>
          </div>
        </Card>
      ) : (
        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <Btn variant="outline" onClick={onBack} style={{ flex: 1 }}>← Back</Btn>
          <Btn onClick={() => onEdit(tape)} style={{ flex: 1 }}>Edit</Btn>
          <Btn variant="danger" onClick={() => setConfirming(true)} style={{ flex: 1 }}>Delete</Btn>
        </div>
      )}
      {!confirming && (
        <Btn variant="gold" onClick={() => onListForSale(tape)} style={{ width: "100%", marginTop: 10 }}>List for Sale / Trade</Btn>
      )}
      <div style={{ height: 20 }} />
    </div>
  );
}
